import { lazy, Suspense, useEffect } from "react";
import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/useAuth";
import { SupplierSidebar } from "@/components/ui/supplier-sidebar";
import ErrorBoundary from "./pages/ErrorBoundatry/ErrorBoundary";

const NotFound = lazy(() => import("@/pages/not-found"));
const Landing = lazy(() => import("@/pages/landing"));
const VendorRegistration = lazy(() => import("@/pages/vendor-registration"));
const RegistrationStatus = lazy(() => import("@/pages/registration-status"));
const PurchaseOrders = lazy(() => import("@/pages/purchase-orders"));
const PurchaseOrderReport = lazy(() => import("@/pages/purchase-order-report"));
const PurchaseOrderView = lazy(() => import("@/pages/purchaseOderView"));
const PurchaseReport = lazy(() => import("@/pages/PurchaseReport"));
const QcReport = lazy(() => import("@/pages/qc-report"));
const QcReportView = lazy(() => import("@/pages/qc-report-view"));
const PurchaseReportView = lazy(() => import("@/pages/PurchaseReportView"));
const PurchaseDebitNoteReport = lazy(
  () => import("@/pages/purchase-debitnote-report"),
);
const PurchaseDebitNoteView = lazy(
  () => import("@/pages/purchaseDebitnoteView"),
);
const PurchaseReturnReport = lazy(
  () => import("@/pages/purchase-return-report"),
);
const PaymentDetailsReport = lazy(
  () => import("@/pages/payment-details-report"),
);
const PaymentDetailsView = lazy(() => import("@/pages/paymentdetailsview"));
const PurchaseReturnView = lazy(() => import("@/pages/PurchaseReturnView"));
const SupplierRegistration = lazy(
  () => import("@/pages/supplier-registration"),
);
const SupplierLogin = lazy(() => import("@/pages/supplier-login"));
const SupplierDashboard = lazy(() => import("@/pages/supplier-dashboard"));
const SupplierProfile = lazy(() => import("@/pages/supplier-profile"));
const ApproverLogin = lazy(() => import("@/pages/approver-login"));
const SimpleApproverDashboard = lazy(
  () => import("@/pages/simple-approver-dashboard"),
);
const SupplierDetailsView = lazy(() => import("@/pages/supplier-details-view"));
const SupplierReview = lazy(() => import("@/pages/supplier-review"));
const UnifiedSignIn = lazy(() => import("@/pages/unified-signin"));
const SupplierPricelist = lazy(() => import("./pages/supplier-pricelist"));
const SupplierInvoiceEntry = lazy(
  () => import("@/pages/supplier-invoice-entry"),
);
const InvoiceList = lazy(() => import("@/pages/invoice-list"));
const InvoicelistView = lazy(() => import("@/pages/InvoicelistView"));

function AuthenticatedLayout({ children }) {
  const [location] = useLocation();
  const hasSupplierSession =
    typeof window !== "undefined" &&
    Boolean(localStorage.getItem("SUPPLIER_ID"));

  const isSupplierPortalRoute =
    location === "/" ||
    location === "/dashboard" ||
    location === "/registration-status" ||
    location === "/purchase-orders" ||
    location === "/payments" ||
    location === "/supplier-dashboard" ||
    location.startsWith("/supplier-dashboard/") ||
    location === "/supplier-profile" ||
    location === "/supplier-pricelist" ||
    location === "/supplier-invoice-entry" ||
    location === "/invoice-list" ||
    location === "/invoicelistview" ||
    location.startsWith("/invoicelistview/") ||
    location === "/reports/purchase-order" ||
    location === "/reports/purchase-report" ||
    location === "/reports/qc-report" ||
    location === "/reports/purchase-debnote" ||
    location === "/reports/purchase-return" ||
    location === "/reports/payment-details" ||
    location.startsWith("/purchaseorderview") ||
    location.startsWith("/purchasereportview") ||
    location.startsWith("/qcreportview") ||
    location.startsWith("/purchasedebitnoteview") ||
    location.startsWith("/purchasereturnview") ||
    location.startsWith("/paymentdetailsview") ||
    location.startsWith("/paymantdetailsview");

  const showSupplierSidebar = hasSupplierSession && isSupplierPortalRoute;

  return (
    <div className="flex h-screen bg-gray-50">
      {showSupplierSidebar ? <SupplierSidebar /> : null}
      <main
        className={`flex-1 overflow-auto ${
          showSupplierSidebar ? "md:ml-64" : ""
        }`}
      >
        {children}
      </main>
    </div>
  );
}

function Router() {
  const { isAuthenticated, isLoading } = useAuth();
  const [, setLocation] = useLocation();
  const hasApproverSession =
    typeof window !== "undefined" &&
    Boolean(localStorage.getItem("approverId"));

  useEffect(() => {
    if (typeof window === "undefined") return;

    const INACTIVITY_MS = 3 * 60 * 60 * 1000; // 3 hours
    const CHECK_INTERVAL_MS = 60 * 1000; // 1 minute
    const LAST_ACTIVITY_KEY = "APP_LAST_ACTIVITY_TS";

    const hasActiveSession = () =>
      Boolean(localStorage.getItem("SUPPLIER_ID")) ||
      Boolean(localStorage.getItem("approverId"));

    const markActivity = () => {
      if (!hasActiveSession()) return;
      localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    };

    const clearSessionData = () => {
      localStorage.setItem("SESSION_EXPIRED_NOTICE", "1");
      localStorage.removeItem("SUPPLIER_ID");
      localStorage.removeItem("supplierDetails");
      localStorage.removeItem("approverId");
      localStorage.removeItem("empId");
      localStorage.removeItem("VID");
      localStorage.removeItem("login-response");
      localStorage.removeItem(LAST_ACTIVITY_KEY);
    };

    const checkInactivity = () => {
      const supplierSession = Boolean(localStorage.getItem("SUPPLIER_ID"));
      const approverSession = Boolean(localStorage.getItem("approverId"));

      if (!supplierSession && !approverSession) return;

      const lastActivity = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || 0);

      if (!lastActivity) {
        markActivity();
        return;
      }

      if (Date.now() - lastActivity >= INACTIVITY_MS) {
        clearSessionData();
        setLocation("/supplier-login");
      }
    };

    const activityEvents = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "click",
    ];

    checkInactivity();
    activityEvents.forEach((eventName) =>
      window.addEventListener(eventName, markActivity, { passive: true }),
    );

    const intervalId = window.setInterval(checkInactivity, CHECK_INTERVAL_MS);

    return () => {
      activityEvents.forEach((eventName) =>
        window.removeEventListener(eventName, markActivity),
      );
      window.clearInterval(intervalId);
    };
  }, [setLocation]);

  const routes =
    isLoading || !isAuthenticated ? (
      <Switch>
        <Route path="/" component={UnifiedSignIn} />
        <Route path="/" component={SupplierLogin} />
        <Route path="/signin" component={UnifiedSignIn} />
        <Route path="/landing" component={Landing} />
        <Route path="/vendor-registration" component={VendorRegistration} />
        <Route path="/supplier-registration" component={SupplierRegistration} />
        <Route path="/supplier-pricelist" component={SupplierPricelist} />
        <Route path="/supplier-login" component={SupplierLogin} />
        <Route path="/supplier-dashboard" component={SupplierDashboard} />
        <Route path="/supplier-profile" component={SupplierProfile} />
        <Route
          path="/supplier-invoice-entry"
          component={SupplierInvoiceEntry}
        />
        <Route path="/invoice-list" component={InvoiceList} />
        <Route path="/invoicelistview/:batchid" component={InvoicelistView} />
        <Route path="/invoicelistview" component={InvoicelistView} />
        <Route
          path="/purchaseorderview/:po_masterid"
          component={PurchaseOrderView}
        />
        <Route path="/purchaseorderview" component={PurchaseOrderView} />
        <Route
          path="/purchasereportview/:po_masterid"
          component={PurchaseReportView}
        />
        <Route path="/purchasereportview" component={PurchaseReportView} />
        <Route
          path="/qcreportview/:po_masterid/:itemid/:vid"
          component={QcReportView}
        />
        <Route path="/qcreportview/:itemid/:vid" component={QcReportView} />
        <Route path="/qcreportview/:po_masterid" component={QcReportView} />
        <Route path="/qcreportview" component={QcReportView} />
        <Route
          path="/purchasedebitnoteview/:po_masterid"
          component={PurchaseDebitNoteView}
        />
        <Route
          path="/purchasedebitnoteview"
          component={PurchaseDebitNoteView}
        />
        <Route
          path="/purchasereturnview/:po_masterid"
          component={PurchaseReturnView}
        />
        <Route path="/purchasereturnview" component={PurchaseReturnView} />
        <Route
          path="/paymentdetailsview/:payadvice_masterid"
          component={PaymentDetailsView}
        />
        <Route path="/paymentdetailsview" component={PaymentDetailsView} />
        <Route
          path="/paymantdetailsview/:payadvice_masterid"
          component={PaymentDetailsView}
        />
        <Route path="/paymantdetailsview" component={PaymentDetailsView} />
        <Route path="/approver-login" component={ApproverLogin} />
        <Route path="/approver-dashboard" component={SimpleApproverDashboard} />
        <Route path="/supplier-details/:vid" component={SupplierDetailsView} />
        <Route
          path="/supplier-review/:supplierVid"
          component={SupplierReview}
        />
        <Route component={NotFound} />
      </Switch>
    ) : (
      <AuthenticatedLayout>
        <Switch>
          <Route
            path="/"
            component={
              hasApproverSession ? SimpleApproverDashboard : SupplierDashboard
            }
          />
          <Route
            path="/dashboard"
            component={
              hasApproverSession ? SimpleApproverDashboard : SupplierDashboard
            }
          />
          <Route path="/approver-login" component={ApproverLogin} />
          <Route
            path="/approver-dashboard"
            component={SimpleApproverDashboard}
          />
          <Route path="/registration-status" component={RegistrationStatus} />
          <Route path="/purchase-orders" component={PurchaseOrders} />
          <Route
            path="/reports/purchase-order"
            component={PurchaseOrderReport}
          />
          <Route
            path="/purchaseorderview/:po_masterid"
            component={PurchaseOrderView}
          />
          <Route path="/purchaseorderview" component={PurchaseOrderView} />
          <Route path="/reports/purchase-report" component={PurchaseReport} />
          <Route path="/reports/qc-report" component={QcReport} />
          <Route
            path="/qcreportview/:po_masterid/:itemid/:vid"
            component={QcReportView}
          />
          <Route path="/qcreportview/:itemid/:vid" component={QcReportView} />
          <Route path="/qcreportview/:po_masterid" component={QcReportView} />
          <Route path="/qcreportview" component={QcReportView} />
          <Route
            path="/reports/purchase-debnote"
            component={PurchaseDebitNoteReport}
          />
          <Route
            path="/reports/purchase-return"
            component={PurchaseReturnReport}
          />
          <Route
            path="/reports/payment-details"
            component={PaymentDetailsReport}
          />
          <Route
            path="/purchasereportview/:po_masterid"
            component={PurchaseReportView}
          />
          <Route path="/purchasereportview" component={PurchaseReportView} />
          <Route
            path="/purchasedebitnoteview/:po_masterid"
            component={PurchaseDebitNoteView}
          />
          <Route
            path="/purchasedebitnoteview"
            component={PurchaseDebitNoteView}
          />
          <Route
            path="/purchasereturnview/:po_masterid"
            component={PurchaseReturnView}
          />
          <Route path="/purchasereturnview" component={PurchaseReturnView} />
          <Route
            path="/paymentdetailsview/:payadvice_masterid"
            component={PaymentDetailsView}
          />
          <Route path="/paymentdetailsview" component={PaymentDetailsView} />
          <Route
            path="/paymantdetailsview/:payadvice_masterid"
            component={PaymentDetailsView}
          />
          <Route path="/paymantdetailsview" component={PaymentDetailsView} />
          <Route path="/payments" component={PaymentDetailsReport} />
          <Route path="/vendor-registration" component={VendorRegistration} />
          <Route
            path="/supplier-registration"
            component={SupplierRegistration}
          />
          <Route path="/supplier-dashboard" component={SupplierDashboard} />
          <Route path="/supplier-dashboard/:id" component={SupplierDashboard} />
          <Route path="/supplier-profile" component={SupplierProfile} />
          <Route path="/supplier-pricelist" component={SupplierPricelist} />
          <Route
            path="/supplier-invoice-entry"
            component={SupplierInvoiceEntry}
          />
          <Route path="/invoice-list" component={InvoiceList} />
          <Route path="/invoicelistview/:batchid" component={InvoicelistView} />
          <Route path="/invoicelistview" component={InvoicelistView} />
          <Route
            path="/supplier-details/:vid"
            component={SupplierDetailsView}
          />
          <Route
            path="/supplier-review/:supplierVid"
            component={SupplierReview}
          />
          <Route component={NotFound} />
        </Switch>
      </AuthenticatedLayout>
    );

  return <Suspense fallback={<div>Loading...</div>}>{routes}</Suspense>;
}

function ErrorFallback({ error }) {
  return (
    <div role="alert" className="p-4 text-red-800 bg-red-100">
      <p>Something went wrong:</p>
      <pre>{error.message}</pre>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <Router />
        </ErrorBoundary>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
